import ReactModal from 'react-modal';
import Button from 'react-bootstrap/Button';
import { modalStyle } from '../../../../../utils/modal';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faXmark } from '@fortawesome/free-solid-svg-icons';
import { CircularProgress } from '@mui/material';
import { Fragment, useState } from 'react';
import { makeRequest } from '../../../../../utils/makeRequest';
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';

function ConsumerEmployeeViewModal({ employeeView, setEmployeeView, closeModal }) {
  const { apiOriginAggregator } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [spinner, setSpinner] = useState(false);
  const [history, setHistory] = useState([]);

  const close = () => {
    setHistory([]);
    closeModal();
  };

  const getFinchEmployee = async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginAggregator}/finch/employees`,
      method: 'POST',
      data: JSON.stringify({
        employees: [
          {
            finchConnectionId: employeeView.connectionId,
            finchEmployeeId: employeeView.managerFinchId,
          },
        ],
      }),
    };

    try {
      const response = await makeRequest(config);

      setHistory(prev => [...prev, employeeView]);
      setEmployeeView({ isManager: true, ...response.employees[0] });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  return (
    <ReactModal
      key={employeeView?.finchId}
      isOpen={!!employeeView}
      style={modalStyle({
        width: '540px',
        backgroundColor: 'rgb(248, 249, 250)',
        overlayBackgroundColor: 'transparent',
        overlayPosition: 'static',
      })}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc
      onRequestClose={close}
    >
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-end gap-3">
          <h5 className="mb-0">{`Employee ${employeeView?.isManager ? '(Manager)' : ''} View`}</h5>
          {history.length > 0 && (
            <Button
              className="bo-common-inline-button text-truncate bo-max-w-200"
              onClick={() => {
                setEmployeeView(history[history.length - 1]);
                setHistory(prev => prev.filter((_, idx) => idx !== history.length - 1));
              }}
            >
              {`Back to ${history[history.length - 1].firstName} 
                ${history[history.length - 1].lastName}`}
            </Button>
          )}
        </div>
        <Button className="bo-common-base-button" onClick={close}>
          <FontAwesomeIcon icon={faXmark} size="lg" color="#000000" />
        </Button>
      </div>
      <div className="bo-h-500 overflow-auto">
        {[
          ['finchId', 'Finch ID'],
          ['sourceId', 'Source ID', true],
          ['firstName', 'First Name'],
          ['middleName', 'Middle Name'],
          ['lastName', 'Last Name'],
          ['preferredName', 'Preferred Name', true],
          ['gender', 'Gender'],
          ['ethnicity', 'Ethnicity'],
          ['dob', 'Date of Birth'],
          ['ssn', 'SSN', true, v => `****${v}`],
        ].map(([key, label, split, format]) => (
          <Fragment key={key}>
            <div className="d-flex">
              <p className="bo-common-text-bold bo-min-w-160">{label}</p>
              <p className="bo-common-text text-truncate">
                {(employeeView && (format ? format(employeeView[key]) : employeeView[key])) ||
                  '---'}
              </p>
            </div>
            {split && <hr className="my-2" />}
          </Fragment>
        ))}
        {[
          ['emails', 'Emails'],
          ['phoneNumbers', 'Phone Numbers'],
        ].map(([key, label]) => (
          <div key={key} className="mb-1">
            <p className="bo-common-text-bold bo-min-w-160">{label}</p>
            {employeeView && employeeView[key]?.length > 0 ? (
              employeeView[key].map(el => (
                <div className="d-flex" key={el.data}>
                  <p className="bo-common-text bo-min-w-160">{el.type}</p>
                  <p className="bo-common-text text-truncate">{el.data}</p>
                </div>
              ))
            ) : (
              <p className="bo-common-text">---</p>
            )}
          </div>
        ))}
        <div>
          <p className="bo-common-text-bold bo-min-w-160">Residence</p>
          {employeeView?.residence ? (
            <>
              <p className="bo-common-text text-truncate">{employeeView.residence.line1}</p>
              <p className="bo-common-text text-truncate">{employeeView.residence.line2}</p>
              <div className="d-flex gap-2">
                <p className="bo-common-text text-truncate bo-w-125">
                  {employeeView.residence.city}
                </p>
                <p className="bo-common-text text-truncate bo-w-40">
                  {employeeView.residence.state}
                </p>
                <p className="bo-common-text text-truncate">{employeeView.residence.postal_code}</p>
              </div>
              <p className="bo-common-text text-truncate">{employeeView.residence.country}</p>
            </>
          ) : (
            <p className="bo-common-text">---</p>
          )}
        </div>
        <hr className="my-2" />
        {[
          ['title', 'Title'],
          ['employmentType', 'Employment Type'],
          ['employmentSubtype', 'Employment Subtype'],
          ['department', 'Department'],
        ].map(([key, label]) => (
          <div key={key} className="d-flex">
            <p className="bo-common-text-bold bo-min-w-160">{label}</p>
            <p className="bo-common-text text-truncate">
              {(employeeView && employeeView[key]) || '---'}
            </p>
          </div>
        ))}
        <div className="d-flex">
          <p className="bo-common-text-bold bo-min-w-160">Manager</p>
          <p className="bo-common-text text-truncate">{employeeView?.managerFinchId || '---'}</p>
          {employeeView?.managerFinchId &&
            (spinner ? (
              <div className="h-100 ms-auto me-4">
                <CircularProgress color="inherit" size={16} />
              </div>
            ) : (
              <Button className="bo-common-base-button ms-auto me-4" onClick={getFinchEmployee}>
                <FontAwesomeIcon icon={faEye} color="#5946A4" size="sm" className="mb-1" />
              </Button>
            ))}
        </div>
        <div className="mb-1">
          <p className="bo-common-text-bold bo-min-w-160">Employment Location</p>
          {employeeView?.employmentLocation ? (
            <>
              <p className="bo-common-text text-truncate">
                {employeeView.employmentLocation.line1}
              </p>
              <p className="bo-common-text text-truncate">
                {employeeView.employmentLocation.line2}
              </p>
              <div className="d-flex gap-2">
                <p className="bo-common-text text-truncate bo-w-125">
                  {employeeView.employmentLocation.city}
                </p>
                <p className="bo-common-text text-truncate bo-w-40">
                  {employeeView.employmentLocation.state}
                </p>
                <p className="bo-common-text text-truncate">
                  {employeeView.employmentLocation.postal_code}
                </p>
              </div>
              <p className="bo-common-text text-truncate">
                {employeeView.employmentLocation.country}
              </p>
            </>
          ) : (
            <p className="bo-common-text">---</p>
          )}
        </div>
        {[
          ['employmentStatus', 'Employment Status'],
          ['isActive', 'Is Active', v => String(v)],
          ['startDate', 'Start Date'],
          ['endDate', 'End Date'],
          ['latestRehireDate', 'Latest Rehire Date'],
        ].map(([key, label, format]) => (
          <div key={key} className="d-flex">
            <p className="bo-common-text-bold bo-min-w-160">{label}</p>
            <p className="bo-common-text text-truncate">
              {(employeeView && (format ? format(employeeView[key]) : employeeView[key])) || '---'}
            </p>
          </div>
        ))}
        <hr className="my-2" />
        <div className="mb-2">
          <p className="bo-common-text-bold bo-min-w-160">Income</p>
          {employeeView?.income ? (
            <>
              <div className="d-flex">
                <p className="bo-common-text bo-min-w-160">Amount</p>
                <p className="bo-common-text text-truncate">
                  {`${employeeView.income.amount} ${employeeView.income.currency}`}
                </p>
              </div>
              <div className="d-flex">
                <p className="bo-common-text bo-min-w-160">Unit</p>
                <p className="bo-common-text text-truncate">{employeeView.income.unit}</p>
              </div>
              <div className="d-flex">
                <p className="bo-common-text bo-min-w-160">Effective Date</p>
                <p className="bo-common-text text-truncate">{employeeView.income.effective_date}</p>
              </div>
            </>
          ) : (
            <p className="bo-common-text">---</p>
          )}
        </div>
        <div className="mb-2">
          <p className="bo-common-text-bold bo-min-w-160">Income History</p>
          {employeeView?.incomeHistory && employeeView.incomeHistory?.length > 0 ? (
            employeeView.incomeHistory.map((x, idx, arr) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={idx}>
                <div className="d-flex">
                  <p className="bo-common-text bo-min-w-160">Amount</p>
                  <p className="bo-common-text text-truncate">{`${x.amount} ${x.currency}`}</p>
                </div>
                <div className="d-flex">
                  <p className="bo-common-text bo-min-w-160">Unit</p>
                  <p className="bo-common-text text-truncate">{x.unit}</p>
                </div>
                <div className="d-flex">
                  <p className="bo-common-text bo-min-w-160">Effective Date</p>
                  <p className="bo-common-text text-truncate">{x.effective_date}</p>
                </div>
                {idx !== arr.length - 1 && <hr className="my-1 bo-dashed" />}
              </Fragment>
            ))
          ) : (
            <p className="bo-common-text">---</p>
          )}
        </div>
      </div>
    </ReactModal>
  );
}

export default ConsumerEmployeeViewModal;
